import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import {
  TrendingUp, DollarSign, Package, Users, Truck, Store,
  Calendar, ArrowUp, ArrowDown, BarChart3, PieChart,
  Clock, CheckCircle, XCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminStatistiques() {
  const [isLoading, setIsLoading] = useState(false);
  const [stats, setStats] = useState({
    revenue: { total: 0, commission: 0, courierPayouts: 0 },
    orders: { total: 0, pending: 0, delivered: 0, cancelled: 0, avgValue: 0 },
    users: { total: 0, clients: 0, partners: 0, couriers: 0 },
    deliveries: { avgDistance: 0, avgDeliveryFee: 0 },
    payments: { wave: 0, orange_money: 0, free_money: 0, cash: 0 },
    daily: []
  });
  const [period, setPeriod] = useState("all");

  const fetchStats = useCallback(async () => {
    setIsLoading(true);
    try {
      const [ordersRes, usersRes] = await Promise.all([
        axios.get(`${API}/orders?limit=1000`),
        axios.get(`${API}/admin/users`)
      ]);

      const orders = ordersRes.data;
      const users = usersRes.data;

      // Filter by period
      const now = new Date();
      const filteredOrders = orders.filter(o => {
        if (period === "all") return true;
        const orderDate = new Date(o.created_at);
        const diffDays = (now - orderDate) / (1000 * 60 * 60 * 24);
        if (period === "today") return diffDays < 1;
        if (period === "week") return diffDays < 7;
        if (period === "month") return diffDays < 30;
        return true;
      });

      // Calculate revenue stats
      const deliveredOrders = filteredOrders.filter(o => o.status === 'delivered');
      const totalRevenue = deliveredOrders.reduce((sum, o) => sum + (o.total_amount || 0), 0);
      const totalCommission = deliveredOrders.reduce((sum, o) => sum + (o.platform_commission || 0), 0);
      const courierPayouts = deliveredOrders.reduce((sum, o) => sum + (o.courier_earning || 0), 0);

      // Calculate order stats
      const avgValue = deliveredOrders.length > 0 ? totalRevenue / deliveredOrders.length : 0;

      // Calculate delivery stats
      const ordersWithDistance = filteredOrders.filter(o => o.distance_km > 0);
      const avgDistance = ordersWithDistance.length > 0
        ? ordersWithDistance.reduce((sum, o) => sum + o.distance_km, 0) / ordersWithDistance.length
        : 0;
      const avgDeliveryFee = deliveredOrders.length > 0
        ? deliveredOrders.reduce((sum, o) => sum + (o.delivery_fee || 0), 0) / deliveredOrders.length
        : 0;

      // Calculate payment method distribution
      const paymentMethods = { wave: 0, orange_money: 0, free_money: 0, cash: 0 };
      filteredOrders.forEach(o => {
        if (paymentMethods.hasOwnProperty(o.payment_method)) {
          paymentMethods[o.payment_method]++;
        }
      });

      // Calculate daily stats (last 7 days)
      const dailyStats = [];
      for (let i = 6; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const dayStart = new Date(date.setHours(0, 0, 0, 0));
        const dayEnd = new Date(date.setHours(23, 59, 59, 999));

        const dayOrders = orders.filter(o => {
          const orderDate = new Date(o.created_at);
          return orderDate >= dayStart && orderDate <= dayEnd;
        });

        const dayDelivered = dayOrders.filter(o => o.status === 'delivered');

        dailyStats.push({
          date: dayStart.toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric' }),
          orders: dayOrders.length,
          delivered: dayDelivered.length,
          revenue: dayDelivered.reduce((sum, o) => sum + (o.total_amount || 0), 0)
        });
      }

      setStats({
        revenue: { total: totalRevenue, commission: totalCommission, courierPayouts },
        orders: {
          total: filteredOrders.length,
          pending: filteredOrders.filter(o => ['pending', 'preparing', 'ready'].includes(o.status)).length,
          delivered: deliveredOrders.length,
          cancelled: filteredOrders.filter(o => o.status === 'cancelled').length,
          avgValue
        },
        users: {
          total: users.length,
          clients: users.filter(u => u.role === 'client').length,
          partners: users.filter(u => u.role === 'partner').length,
          couriers: users.filter(u => u.role === 'courier').length
        },
        deliveries: { avgDistance, avgDeliveryFee },
        payments: paymentMethods,
        daily: dailyStats
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Erreur lors du chargement des statistiques");
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(Math.round(amount)) + ' FCFA';
  };

  const getPaymentTotal = () => {
    return stats.payments.wave + stats.payments.orange_money + stats.payments.free_money + stats.payments.cash;
  };

  const getPaymentPercentage = (method) => {
    const total = getPaymentTotal();
    if (total === 0) return 0;
    return Math.round((stats.payments[method] / total) * 100);
  };

  return (
    <div className="space-y-6">
      {/* Period Filter */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold" style={{fontFamily: 'Outfit, sans-serif'}}>
          Tableau de bord statistiques
        </h2>
        <div className="flex gap-2">
          {[
            { value: "today", label: "Aujourd'hui" },
            { value: "week", label: "7 jours" },
            { value: "month", label: "30 jours" },
            { value: "all", label: "Tout" }
          ].map(p => (
            <Button
              key={p.value}
              variant={period === p.value ? "default" : "outline"}
              size="sm"
              className="rounded-full"
              onClick={() => setPeriod(p.value)}
            >
              {p.label}
            </Button>
          ))}
          <Button onClick={fetchStats} variant="outline" size="sm" className="rounded-full">
            🔄
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Chargement des statistiques...</div>
      ) : (
        <>
          {/* Revenue Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm opacity-80">Chiffre d'affaires</p>
                    <p className="text-3xl font-bold mt-2">{formatCurrency(stats.revenue.total)}</p>
                    <p className="text-xs mt-2 opacity-80">{stats.orders.delivered} commandes livrées</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-xl">
                    <DollarSign className="w-8 h-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm opacity-80">Commission plateforme (10%)</p>
                    <p className="text-3xl font-bold mt-2">{formatCurrency(stats.revenue.commission)}</p>
                    <p className="text-xs mt-2 opacity-80">Bénéfice ARTEKNOS</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-xl">
                    <TrendingUp className="w-8 h-8" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0 shadow-lg">
              <CardContent className="pt-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm opacity-80">Paiements livreurs</p>
                    <p className="text-3xl font-bold mt-2">{formatCurrency(stats.revenue.courierPayouts)}</p>
                    <p className="text-xs mt-2 opacity-80">Total versé aux livreurs</p>
                  </div>
                  <div className="bg-white/20 p-3 rounded-xl">
                    <Truck className="w-8 h-8" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Orders & Users Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Orders Breakdown */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5" />
                  Répartition des commandes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total commandes</span>
                    <span className="text-2xl font-bold">{stats.orders.total}</span>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4 text-yellow-500" /> En cours
                        </span>
                        <span>{stats.orders.pending}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-500 rounded-full transition-all"
                          style={{ width: `${stats.orders.total > 0 ? (stats.orders.pending / stats.orders.total * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-1">
                          <CheckCircle className="w-4 h-4 text-green-500" /> Livrées
                        </span>
                        <span>{stats.orders.delivered}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-green-500 rounded-full transition-all"
                          style={{ width: `${stats.orders.total > 0 ? (stats.orders.delivered / stats.orders.total * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="flex items-center gap-1">
                          <XCircle className="w-4 h-4 text-red-500" /> Annulées
                        </span>
                        <span>{stats.orders.cancelled}</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-red-500 rounded-full transition-all"
                          style={{ width: `${stats.orders.total > 0 ? (stats.orders.cancelled / stats.orders.total * 100) : 0}%` }}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="pt-4 border-t">
                    <p className="text-sm text-muted-foreground">Valeur moyenne par commande</p>
                    <p className="text-xl font-bold text-primary">{formatCurrency(stats.orders.avgValue)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Users Breakdown */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Utilisateurs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Total utilisateurs</span>
                    <span className="text-2xl font-bold">{stats.users.total}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-xl">
                      <Users className="w-8 h-8 text-blue-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-blue-700">{stats.users.clients}</p>
                      <p className="text-xs text-blue-600">Clients</p>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-xl">
                      <Store className="w-8 h-8 text-green-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">{stats.users.partners}</p>
                      <p className="text-xs text-green-600">Partenaires</p>
                    </div>
                    <div className="text-center p-4 bg-orange-50 rounded-xl">
                      <Truck className="w-8 h-8 text-orange-500 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-orange-700">{stats.users.couriers}</p>
                      <p className="text-xs text-orange-600">Livreurs</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Payment Methods & Delivery Stats */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Payment Methods */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <PieChart className="w-5 h-5" />
                  Méthodes de paiement
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {[
                    { key: 'wave', label: 'Wave', color: 'bg-[#D4AF37]' },
                    { key: 'orange_money', label: 'Orange Money', color: 'bg-[#FF6600]' },
                    { key: 'free_money', label: 'Free Money', color: 'bg-[#E30613]' },
                    { key: 'cash', label: 'Espèces', color: 'bg-green-500' }
                  ].map(method => (
                    <div key={method.key}>
                      <div className="flex justify-between text-sm mb-1">
                        <span>{method.label}</span>
                        <span className="font-medium">{stats.payments[method.key]} ({getPaymentPercentage(method.key)}%)</span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full ${method.color} rounded-full transition-all`}
                          style={{ width: `${getPaymentPercentage(method.key)}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Delivery Stats */}
            <Card className="shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5" />
                  Statistiques de livraison
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">Distance moyenne</p>
                    <p className="text-3xl font-bold text-slate-700">{stats.deliveries.avgDistance.toFixed(1)}</p>
                    <p className="text-xs text-muted-foreground">kilomètres</p>
                  </div>
                  <div className="text-center p-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-muted-foreground mb-1">Frais moyen</p>
                    <p className="text-3xl font-bold text-slate-700">{Math.round(stats.deliveries.avgDeliveryFee)}</p>
                    <p className="text-xs text-muted-foreground">FCFA</p>
                  </div>
                </div>

                {/* Mini chart - daily orders */}
                <div className="mt-6 pt-4 border-t">
                  <p className="text-sm font-semibold mb-3">Commandes des 7 derniers jours</p>
                  <div className="flex items-end justify-between gap-1 h-24">
                    {stats.daily.map((day) => (
                      <div key={day.date || `${day.day}-${day.orders}`} className="flex-1 flex flex-col items-center">
                        <div
                          className="w-full bg-primary/80 rounded-t transition-all hover:bg-primary"
                          style={{
                            height: `${stats.daily.length > 0 && Math.max(...stats.daily.map(d => d.orders)) > 0
                              ? (day.orders / Math.max(...stats.daily.map(d => d.orders))) * 80
                              : 0}px`,
                            minHeight: day.orders > 0 ? '4px' : '0'
                          }}
                          title={`${day.orders} commandes`}
                        />
                        <span className="text-[10px] text-muted-foreground mt-1">{day.date}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}
